import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DONATION_URL = process.env.REACT_APP_DONATION_URL;
const SCALE = [
  { value: 1, short: "Not true", label: "Not true for me" },
  { value: 2, short: "Rarely", label: "Rarely true" },
  { value: 3, short: "Sometimes", label: "Sometimes true" },
  { value: 4, short: "Often", label: "Often true" },
  { value: 5, short: "True", label: "True for me" },
];
const DESCRIPTIONS = {
  S: "Connection to Source, intuition, divine guidance and spiritual identity.",
  P: "Emotional awareness, mental clarity, and the beliefs underneath your behaviour.",
  A: "Identity congruence, truth, alignment and self-expression.",
  C: "Covenant identity, apostolic instruction, mystical essence and Christ embodiment.",
  E: "Vitality, frequency and presence — and the work and offers that carry your gift.",
};
const EMPTY = { S: [], P: [], A: [], C: [], E: [] };
const DEADLINE_KEY = "space_offer_deadline";

function useOfferDeadline(expiresAt) {
  const [deadline] = useState(() => {
    const mine = new Date(expiresAt).getTime();
    const saved = Number(localStorage.getItem(DEADLINE_KEY));
    const d = saved && saved < mine ? saved : mine;
    localStorage.setItem(DEADLINE_KEY, String(d));
    return d;
  });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return Math.max(0, deadline - now);
}

function Countdown({ ms }) {
  const pad = (n) => String(n).padStart(2, "0");
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return (
    <p className="fa-countdown" data-testid="fa-offer-countdown">
      This offer is held for you for <strong>{pad(h)}:{pad(m)}:{pad(sec)}</strong>
    </p>
  );
}

function ThirtyDays({ plan }) {
  return (
    <div className="fa-thirty" data-testid="fa-thirty-days">
      <h3 className="fa-next-title">Your thirty days</h3>
      <p className="fa-thirty-sub">Six days on each pillar, lowest first.</p>
      <ol className="fa-thirty-list">
        {plan.map((d) => (
          <li key={d.pillar} data-testid={`fa-thirty-${d.pillar}`}>
            <span className="fa-thirty-days">{d.days}</span>
            <span className="fa-thirty-name">{d.pillar} · {d.name}</span>
            <span className="fa-thirty-practice">{d.practice}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Question({ pillar, index, text, value, onPick }) {
  return (
    <div className="diag-question" data-testid={`fa-q-${pillar}-${index}`}>
      <div className="diag-question-text">
        <span className="diag-question-num">{index + 1}.</span> {text}
      </div>
      <div className="diag-likert-row">
        {SCALE.map((s) => (
          <button
            key={s.value}
            type="button"
            title={s.label}
            className={`diag-likert-btn ${value === s.value ? "selected" : ""}`}
            onClick={() => onPick(s.value)}
            data-testid={`fa-q-${pillar}-${index}-opt-${s.value}`}
          >
            <span className="diag-likert-short fa-likert-short">{s.short}</span>
            <span className="diag-likert-num">{s.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Progress({ pillars, names, idx, responses, answered, onJump }) {
  return (
    <>
      <div className="fa-count" data-testid="fa-answered-count">{answered} of 60 answered</div>
      <div className="diag-progress">
        {pillars.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onJump(i)}
            className={`diag-progress-step ${i === idx ? "active" : ""} ${responses[p].filter(Boolean).length === 12 ? "done" : ""}`}
            data-testid={`fa-progress-${p}`}
          >
            <span className="diag-progress-letter">{p}</span>
            <span className="diag-progress-name">{names[p]}</span>
          </button>
        ))}
      </div>
      <div className="diag-progress-bar">
        <div className="diag-progress-fill" style={{ width: `${(answered / 60) * 100}%` }} />
      </div>
    </>
  );
}

function EmailMapForm({ resultId }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  async function send(e) {
    e.preventDefault();
    setState("sending");
    try {
      const { data } = await axios.post(`${API}/assessment/email`, { result_id: resultId, email: email.trim(), name: name.trim() || null });
      setState(data.sent ? "sent" : "saved");
    } catch {
      setState("error");
    }
  }
  if (state === "saved") {
    return <p className="fa-email-done fa-email-saved" data-testid="fa-email-saved">Your map is saved and Dr. Rogers has been notified. The PDF couldn't be emailed just now — he will send it to {email} personally.</p>;
  }
  if (state === "sent") {
    return <p className="fa-email-done" data-testid="fa-email-sent">Your Identity Map PDF is on its way to {email}.</p>;
  }
  return (
    <form className="fa-email" onSubmit={send} data-testid="fa-email-form">
      <h4>Email me my Identity Map (optional)</h4>
      <div className="fa-email-row">
        <input type="text" placeholder="First name (optional)" value={name} onChange={(e) => setName(e.target.value)}
          maxLength={120} data-testid="fa-email-name" />
        <input type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="fa-email-input" />
        <button type="submit" className="btn-primary" disabled={state === "sending"} data-testid="fa-email-submit">
          {state === "sending" ? "Sending…" : "Send My Map"}
        </button>
      </div>
      {state === "error" && <p className="diag-error" data-testid="fa-email-error">That didn't send. Please check the address and try again.</p>}
    </form>
  );
}

function Offers({ lowestName, expiresAt }) {
  const left = useOfferDeadline(expiresAt);
  return (
    <div className="fa-offers" data-testid="fa-offers">
      <div className="fa-offer">
        <span className="fa-offer-tag">Free</span>
        <h4>Free Live Reading</h4>
        <p>30 minutes, live and all-inclusive — S.P.A.C.E. Identity Engineering and S.P.A.C.E. Identity Transformation.</p>
        <a href="/book-reading" className="btn-primary" data-testid="fa-offer-reading">Book My Free Reading →</a>
      </div>
      <div className="fa-offer">
        <span className="fa-offer-tag">One Pillar</span>
        <h4>One-Hour Identity Transformation</h4>
        <p className="fa-offer-price" data-testid="fa-offer-one-pillar-price"><strong>$999</strong></p>
        <p>One hour with Dr. Rogers on a single pillar — starting with your lowest: <strong>{lowestName}</strong>.</p>
        <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="fa-offer-one-pillar">Reserve My Hour →</a>
      </div>
      {left > 0 ? (
        <div className="fa-offer fa-offer-flagship">
        <span className="fa-offer-tag">One-Time Offer · 50% Off</span>
        <h4>Full Five-Pillar S.P.A.C.E. Transformation</h4>
        <p className="fa-offer-price"><s>$5,000</s> <strong>$2,500</strong> paid in full</p>
        <p>All five pillars rebuilt with Dr. Rogers. This price is offered only here, with your Identity Map.</p>
        <Countdown ms={left} />
        <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="fa-offer-full">Claim 50% Off →</a>
      </div>
      ) : (
        <div className="fa-offer fa-offer-closed" data-testid="fa-offer-closed">
          <span className="fa-offer-tag">One-Time Offer</span>
          <h4>This offer has closed</h4>
          <p>The 48-hour window for the 50% five-pillar offer has passed.</p>
        </div>
      )}
    </div>
  );
}

function Results({ result, names, pillars, onRetake }) {
  return (
    <div className="fa-results" data-testid="fa-results">
      <div className="diag-kicker">Your Identity Map</div>
      <h3 className="fa-results-title" data-testid="fa-overall">{result.overall_tier_label}</h3>
      <p className="fa-results-total" data-testid="fa-total">{result.total_score} / 300</p>
      <p className="fa-results-interp">{result.overall_interpretation}</p>
      <div className="diag-summary-grid fa-summary-grid">
        {pillars.map((p) => (
          <div className={`diag-summary-card ${p === result.lowest_pillar ? "fa-lowest" : ""}`} key={p} data-testid={`fa-result-${p}`}>
            <div className="diag-summary-letter">{p}</div>
            <div className="diag-summary-name">{names[p]}</div>
            <div className="diag-summary-score">{result.pillar_scores[p]} / 60</div>
            <div className="diag-summary-tier">{result.pillar_tiers[p].label}</div>
            {p === result.lowest_pillar && <div className="fa-lowest-tag">Begin here</div>}
          </div>
        ))}
      </div>
      <EmailMapForm resultId={result.id} />
      <ThirtyDays plan={result.thirty_days} />
      <h3 className="fa-next-title">Turn this map into direction</h3>
      <Offers lowestName={names[result.lowest_pillar]} expiresAt={result.offer_expires_at} />
      <button type="button" className="btn-ghost fa-retake" onClick={onRetake} data-testid="fa-retake">Retake the assessment</button>
    </div>
  );
}

export const FreeAssessment = () => {
  const [meta, setMeta] = useState(null);
  const [idx, setIdx] = useState(0);
  const [responses, setResponses] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const topRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/assessment`).then((r) => setMeta(r.data)).catch(() => setError("The assessment could not load. Please refresh."));
  }, []);

  const scrollTop = () => setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

  if (!meta) return <div className="space-diag fa-embed" data-testid="fa-loading"><p className="diag-loading">{error || "Loading…"}</p></div>;

  const { pillars, pillar_names: names, questions } = meta;
  const pillar = pillars[idx];
  const answered = pillars.reduce((n, p) => n + responses[p].filter(Boolean).length, 0);
  const pillarDone = responses[pillar].filter(Boolean).length === 12;

  function pick(qi, v) {
    const arr = [...responses[pillar]];
    while (arr.length < 12) arr.push(null);
    arr[qi] = v;
    setResponses({ ...responses, [pillar]: arr });
  }
  function go(i) { setIdx(i); setError(""); scrollTop(); }

  async function submit() {
    const missing = pillars.find((p) => responses[p].filter(Boolean).length < 12);
    if (missing) { setError(`Please answer all 12 ${names[missing]} questions.`); setIdx(pillars.indexOf(missing)); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/assessment/score`, { responses });
      setResult(data);
      scrollTop();
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-diag fa-embed" ref={topRef} data-testid="free-assessment">
      {result ? (
        <Results result={result} names={names} pillars={pillars}
          onRetake={() => { setResult(null); setResponses(EMPTY); go(0); }} />
      ) : (
        <>
          <Progress pillars={pillars} names={names} idx={idx} responses={responses} answered={answered} onJump={go} />
          <div className="diag-pillar-card" data-testid={`fa-pillar-${pillar}`}>
            <div className="diag-pillar-header">
              <span className="diag-pillar-letter">{pillar}</span>
              <div><h2>{names[pillar]}</h2><p>{DESCRIPTIONS[pillar]}</p></div>
            </div>
            <p className="fa-hint">Answer what is true for you lately, not what you wish were true.</p>
            <div className="diag-questions">
              {questions[pillar].map((q, qi) => (
                <Question key={qi} pillar={pillar} index={qi} text={q} value={responses[pillar][qi]} onPick={(v) => pick(qi, v)} />
              ))}
            </div>
            <div className="diag-nav">
              <button type="button" className="btn-ghost" disabled={idx === 0} onClick={() => go(idx - 1)} data-testid="fa-prev">← Back</button>
              {idx < pillars.length - 1 ? (
                <button type="button" className="btn-primary" disabled={!pillarDone} onClick={() => go(idx + 1)} data-testid="fa-next">
                  Next: {names[pillars[idx + 1]]} →
                </button>
              ) : (
                <button type="button" className="btn-primary" disabled={submitting || answered < 60} onClick={submit} data-testid="fa-submit">
                  {submitting ? "Mapping…" : "See My Identity Map"}
                </button>
              )}
            </div>
            {error && <p className="diag-error" data-testid="fa-error">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
};
